import type { CapacityMark } from '@/components/ui/capacity-meter';
import type { ChannelInfo, ChannelVerdict, Matrix } from './types';
import { BAR_MAX_MB } from './feChannelConfig';
import { iconForFamily } from './channelIcons';
import { ChannelIcon } from './SizeBar';

/** Each platform family's size limit as a mark on the story's size meter: heavy where a platform warns, exceeded where one blocks. */
export function sizeMarks(matrix: Matrix, verdicts: ChannelVerdict[]): CapacityMark[] {
  const verdictByKey = new Map(verdicts.map(v => [v.key, v]));
  const byMB = new Map<number, { channels: ChannelInfo[]; statuses: ChannelVerdict['status'][] }>();
  for (const channel of matrix.channels) {
    const threshold = matrix.sizeThresholds[channel.key].warnMB;
    if (!byMB.has(threshold)) byMB.set(threshold, { channels: [], statuses: [] });
    const bucket = byMB.get(threshold)!;
    bucket.channels.push(channel);
    const verdict = verdictByKey.get(channel.key);
    if (verdict) bucket.statuses.push(verdict.status);
  }
  return [...byMB.entries()]
    .sort(([a], [b]) => a - b)
    .map(([thresholdMB, { channels, statuses }]) => {
      const families = new Map<string, string[]>();
      for (const channel of channels) {
        const platform = channel.device === 'spectacles' ? '' : channel.device === 'ios' ? 'iOS' : 'Android';
        if (!families.has(channel.family)) families.set(channel.family, []);
        if (platform) families.get(channel.family)!.push(platform);
      }
      const icons = [...new Set([...families.keys()].map(iconForFamily))];
      return {
        at: Math.min(thresholdMB / BAR_MAX_MB, 1),
        state: statuses.includes('no') ? 'blocked' : statuses.includes('warn') ? 'warn' : 'ok',
        label: (
          <div className="flex items-center gap-2">
            <span className="font-medium">
              {[...families.entries()].map(([family, platforms]) => (platforms.length ? `${family} (${platforms.join('/')})` : family)).join(' · ')}
            </span>
            <span className="tabular-nums text-lureo-ink-subtlest">{thresholdMB} MB</span>
          </div>
        ),
        above: thresholdMB,
        below: icons.map(icon => <ChannelIcon key={icon} type={icon} />),
      } satisfies CapacityMark;
    });
}
